import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import recSummer from "./recSummer.png";
import recWinter from "./recWinter.png";

const HeatRecovery = () => {
  const { t } = useTranslation();
  const { watch } = useFormContext();

  // Watch form values
  const summerOutdoorTemperature = watch("summerOutdoorTemperature");
  const summerOutdoorHumidity = watch("summerOutdoorHumidity");
  const winterOutdoorTemperature = watch("winterOutdoorTemperature");
  const winterOutdoorHumidity = watch("winterOutdoorHumidity");
  const summerIndoorTemperature = watch("summerIndoorTemperature");
  const summerIndoorHumidity = watch("summerIndoorHumidity");
  const winterIndoorTemperature = watch("winterIndoorTemperature");
  const winterIndoorHumidity = watch("winterIndoorHumidity");
  const summerRecCalculation = watch("summerRecCalculation");
  const winterRecCalculation = watch("winterRecCalculation");

  // Extract values from recVal response
  const summerValues = useMemo(() => {
    if (!summerRecCalculation) {
      return {
        supTemp: "",
        supHumidity: "",
        ehaTemp: "",
        ehaHumidity: "",
      };
    }
    return {
      supTemp: summerRecCalculation?.sup_AirTempOut ?? "",
      supHumidity: summerRecCalculation?.sup_HumidityOut ?? "",
      ehaTemp: summerRecCalculation?.eha_AirTempOut ?? "",
      ehaHumidity: summerRecCalculation?.eha_HumidityOut ?? "",
    };
  }, [summerRecCalculation]);

  const winterValues = useMemo(() => {
    if (!winterRecCalculation) {
      return {
        supTemp: "",
        supHumidity: "",
        ehaTemp: "",
        ehaHumidity: "",
      };
    }
    return {
      supTemp: winterRecCalculation?.sup_AirTempOut ?? "",
      supHumidity: winterRecCalculation?.sup_HumidityOut ?? "",
      ehaTemp: winterRecCalculation?.eha_AirTempOut ?? "",
      ehaHumidity: winterRecCalculation?.eha_HumidityOut ?? "",
    };
  }, [winterRecCalculation]);

  // Sensor Box Component
  const SensorBox = ({ title, temperature, humidity, position }) => {
    const positionStyles = {
      "left-top": {
        position: "absolute",
        left: { xs: "-38%", sm: "-35%", md: "-32%", lg: "-30%" },
        top: "20%",
        transform: "translateY(-50%)",
      },
      "left-bottom": {
        position: "absolute",
        left: { xs: "-38%", sm: "-35%", md: "-32%", lg: "-30%" },
        bottom: "25%",
        transform: "translateY(-50%)",
      },
      "right-top": {
        position: "absolute",
        right: { xs: "-38%", sm: "-35%", md: "-32%", lg: "-30%" },
        top: "20%",
        transform: "translateY(-50%)",
      },
      "right-bottom": {
        position: "absolute",
        right: { xs: "-38%", sm: "-35%", md: "-32%", lg: "-30%" },
        bottom: "25%",
        transform: "translateY(-50%)",
      },
    };

    const isETA = title === "ETA";
    const temperatureLabel = isETA
      ? t("operatingpoint.summerIndoorTemperature") || "Indoor temperature"
      : t("operatingpoint.dryBulbTemperatureCelsius") || "Dry bulb temperature";
    const humidityLabel = isETA
      ? t("operatingpoint.summerIndoorHumidity") || "Indoor humidity"
      : t("operatingpoint.relativeHumiditySummer") || "Relative humidity";

    const displayValue = (value) =>
      value !== "" && value !== undefined ? value : "";

    return (
      <Box
        sx={{
          ...positionStyles[position],
          width: { xs: "130px", sm: "150px", md: "170px", lg: "190px" },
          minWidth: { xs: "130px", sm: "150px", md: "170px", lg: "190px" },
          maxWidth: { xs: "130px", sm: "150px", md: "170px", lg: "190px" },
          display: "flex",
          flexDirection: "column",
          gap: "15px",
          zIndex: 2,
        }}
      >
        <TextField
          disabled
          fullWidth
          id={`${title}-temp`}
          label={temperatureLabel}
          value={displayValue(temperature)}
          type="number"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: <InputAdornment position="start">°C</InputAdornment>,
          }}
        />
        <TextField
          disabled
          fullWidth
          id={`${title}-humidity`}
          label={humidityLabel}
          value={displayValue(humidity)}
          type="number"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: <InputAdornment position="start">%</InputAdornment>,
          }}
        />
      </Box>
    );
  };

  // Diagram with Sensor Boxes Component
  const DiagramWithSensors = ({
    image,
    seasonIcon,
    odaTemp,
    odaHumidity,
    supTemp,
    supHumidity,
    etaTemp,
    etaHumidity,
    ehaTemp,
    ehaHumidity,
  }) => (
    <Box
      sx={{
        position: "relative",
        display: { xs: "block", md: "flex" },
        justifyContent: "center",
        alignItems: "center",
        mb: 4,
        minHeight: { xs: "300px", md: "400px" },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: { xs: "10px", md: "20px" },
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10,
        }}
      >
        {seasonIcon}
      </Box>

      <Box
        sx={{
          width: { xs: "100%", md: "60%" },
          position: "relative",
          display: { xs: "none", md: "flex" },
          justifyContent: "center",
          alignItems: "center",
          overflow: "visible",
          minHeight: { md: "400px", lg: "500px" },
        }}
      >
        <Box
          component="img"
          src={image}
          alt="Heat recovery diagram"
          sx={{
            width: "100%",
            height: "auto",
            objectFit: "contain",
            position: "relative",
          }}
        />

        <SensorBox
          title="ODA"
          temperature={odaTemp}
          humidity={odaHumidity}
          position="left-bottom"
        />
        <SensorBox
          title="EHA"
          temperature={ehaTemp}
          humidity={ehaHumidity}
          position="left-top"
        />
        <SensorBox
          title="SUP"
          temperature={supTemp}
          humidity={supHumidity}
          position="right-bottom"
        />
        <SensorBox
          title="ETA"
          temperature={etaTemp}
          humidity={etaHumidity}
          position="right-top"
        />
      </Box>

      <Box
        sx={{
          display: { xs: "block", md: "none" },
          width: "100%",
          mb: 2,
        }}
      >
        <Box
          component="img"
          src={image}
          alt="Heat recovery diagram"
          sx={{
            width: "100%",
            height: "auto",
          }}
        />
      </Box>
    </Box>
  );

  return (
    <Container component="main" sx={{ mb: 4, "min-width": "100%" }}>
      <Paper
        variant="outlined"
        sx={{ p: { xs: 2, md: "15px 24px 24px 24px" }, align: "center" }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
          <Typography sx={{ textAlign: "center", fontSize: "38px", mb: "0px" }}>
            {t("mainform.heatRecovery") || "Heat Recovery"}
          </Typography>
        </Box>

        {/* Legend */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: { xs: 2, md: 4 },
            mb: 4,
            mt: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography sx={{ fontWeight: "bold" }}>ODA</Typography>
            <Typography>–</Typography>
            <Typography>{t("heatRecovery.freshAir") || "Fresh air"}</Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography sx={{ fontWeight: "bold" }}>SUP</Typography>
            <Typography>–</Typography>
            <Typography>
              {t("heatRecovery.supplyAir") || "Supply air"}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography sx={{ fontWeight: "bold" }}>ETA</Typography>
            <Typography>–</Typography>
            <Typography>
              {t("heatRecovery.returnAir") || "Return air"}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography sx={{ fontWeight: "bold" }}>EHA</Typography>
            <Typography>–</Typography>
            <Typography>
              {t("heatRecovery.exhaustAir") || "Exhaust air"}
            </Typography>
          </Box>
        </Box>

        <DiagramWithSensors
          image={recSummer}
          seasonIcon={
            <WbSunnyIcon
              sx={{
                fontSize: { xs: "32px", md: "48px" },
                color: "#FFA500",
              }}
            />
          }
          odaTemp={summerOutdoorTemperature}
          odaHumidity={summerOutdoorHumidity}
          supTemp={summerValues.supTemp}
          supHumidity={summerValues.supHumidity}
          etaTemp={summerIndoorTemperature}
          etaHumidity={summerIndoorHumidity}
          ehaTemp={summerValues.ehaTemp}
          ehaHumidity={summerValues.ehaHumidity}
        />

        <DiagramWithSensors
          image={recWinter}
          seasonIcon={
            <AcUnitIcon
              sx={{
                fontSize: { xs: "32px", md: "48px" },
                color: "#87CEEB",
              }}
            />
          }
          odaTemp={winterOutdoorTemperature}
          odaHumidity={winterOutdoorHumidity}
          supTemp={winterValues.supTemp}
          supHumidity={winterValues.supHumidity}
          etaTemp={winterIndoorTemperature}
          etaHumidity={winterIndoorHumidity}
          ehaTemp={winterValues.ehaTemp}
          ehaHumidity={winterValues.ehaHumidity}
        />
      </Paper>
    </Container>
  );
};

export default HeatRecovery;
